import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import { DataTools, RowDelete } from "./DataTools";
import { api, formatApiError } from "@/lib/api";
import { Chips, Empty, Loading, PageHead, Stat, fmtWhen, money, useAdmin } from "./ui";
import { PAY_METHODS, ReportFilters, SalesChart, saleDay, ymd } from "./ShopSalesReport";

const STATUSES = ["paid", "created", "refunded", "failed"];
const STATUS_LABEL = { paid: "paid", created: "started, not paid", refunded: "refunded", failed: "failed" };
const METHODS = ["paypal", "card", "card_in_store", "cash", "bank_transfer", "other"];
const methodName = (m) => (PAY_METHODS.find(([k]) => k === m) || ["", m || "—"])[1];
const asOrder = (p) => ({ ...p, total: p.amount, payment_method: p.provider });

const PaymentRow = ({ p, onChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(p.admin_notes || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/payments/${p.id}`, body); toast.success(msg); onChange(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className={`req st-${p.status}`} data-testid={`payment-row-${p.id}`}>
      <button type="button" className="order-head" onClick={() => setOpen(!open)} data-testid="payment-toggle">
        <span><strong>{p.client_name}</strong> · {p.treatment}</span>
        <span className={`pill pay-${p.status}`}>{STATUS_LABEL[p.status] || p.status}</span>
        <small>{fmtWhen(p.paid_at || p.created_at)} · {methodName(p.provider)} · <b>{money(p.amount)}</b></small>
      </button>
      {open && (
        <div className="order-body" data-testid="payment-details">
          <p className="order-item">Appointment <b>{p.booking_date ? `${p.booking_date} · ${p.time_label}` : "—"}</b></p>
          <p className="order-item">Treatment price <b>{p.price || "—"}</b></p>
          <p className="order-item">Booking status <b>{p.booking_status?.replace("_", " ") || "—"}</b></p>
          {(p.capture_id || p.order_id) && <p className="order-item">Reference <b>{p.capture_id || p.order_id}</b></p>}
          <div className="req-actions">
            {p.phone && <a className="btn light" href={`tel:${p.phone}`} data-testid="payment-call-button"><Phone size={14} /> Call</a>}
            {p.client_email && <a className="btn light" href={`mailto:${p.client_email}?subject=${encodeURIComponent(`Your Wax Moi booking — ${p.treatment}`)}`} data-testid="payment-email-button"><Mail size={14} /> Email</a>}
          </div>
          <div className="order-ctl">
            <label className="mu-field">Status
              <select value={p.status} onChange={(e) => patch({ status: e.target.value }, "Status updated")} data-testid="payment-status-select">
                {[...new Set([...STATUSES, p.status])].map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
              </select>
            </label>
            <label className="mu-field">Paid by
              <select value={p.provider} onChange={(e) => patch({ provider: e.target.value }, "Payment method saved")} data-testid="payment-method-select">
                {[...new Set([...METHODS, p.provider])].map((m) => <option key={m} value={m}>{methodName(m)}</option>)}
              </select>
            </label>
          </div>
          <textarea rows={2} placeholder="Private notes" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (p.admin_notes || "") && patch({ admin_notes: notes }, "Notes saved")} data-testid="payment-notes-input" />
          <RowDelete url={`/admin/payments/${p.id}`} name={`this ${money(p.amount)} payment from ${p.client_name}`} onDone={onChange} testId="payment-delete-button" />
        </div>
      )}
    </div>
  );
};

const Totals = ({ list, owed }) => {
  const paid = list.filter((p) => p.status === "paid");
  const sum = paid.reduce((n, p) => n + p.amount, 0);
  return (
    <div className="ad-stats" data-testid="payments-summary">
      <Stat label="Collected" value={money(sum)} testId="payments-total" />
      <Stat label="Paid payments" value={paid.length} hint={`${list.length} total`} testId="payments-stat-paid" />
      <Stat label="Average payment" value={money(paid.length ? sum / paid.length : 0)} testId="payments-stat-average" />
      <Stat label="Refunded" value={money(list.filter((p) => p.status === "refunded").reduce((n, p) => n + p.amount, 0))} testId="payments-stat-refunded" />
      <Stat label="Balances due" value={owed ? money(owed.total) : "…"} hint={owed ? `${owed.bookings.length} confirmed bookings` : ""} testId="payments-stat-owed" />
    </div>
  );
};

const TopTreatments = ({ list }) => {
  const top = Object.values(list.filter((p) => p.status === "paid").reduce((m, p) => {
    const k = p.treatment || "—";
    m[k] = { name: k, n: (m[k]?.n || 0) + 1, revenue: (m[k]?.revenue || 0) + p.amount, ids: (m[k]?.ids || new Set()).add(p.booking_id) };
    return m;
  }, {})).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  if (!top.length) return null;
  return (
    <div className="ad-card" data-testid="payments-top-treatments">
      <h3>Top treatments</h3>
      {top.map((t) => <div className="ad-row" key={t.name}><span>{t.name} · {t.ids.size} booking{t.ids.size > 1 ? "s" : ""}</span><strong>{money(t.revenue)}</strong></div>)}
    </div>
  );
};

const PaymentsTable = ({ list, from, to }) => (
  <div className="ad-card" data-testid="payments-table">
    <div className="sales-table-head"><h3>Payments {from} → {to}</h3></div>
    {!list.length ? <Empty>No payments in this range.</Empty> : (
      <div className="sales-table-wrap">
        <table className="sales-table">
          <thead><tr><th>Date</th><th>Client</th><th>Treatment</th><th>Paid by</th><th>Status</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} data-testid={`payments-table-row-${p.id}`}>
                <td>{saleDay(p)}</td><td>{p.client_name}</td><td>{p.treatment}</td><td>{methodName(p.provider)}</td>
                <td><span className={`pill pay-${p.status}`}>{STATUS_LABEL[p.status] || p.status}</span></td><td className="num">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={5}>Collected total</td><td className="num" data-testid="payments-table-total">{money(list.filter((p) => p.status === "paid").reduce((n, p) => n + p.amount, 0))}</td></tr></tfoot>
        </table>
      </div>
    )}
  </div>
);

const Owed = ({ owed }) => (!owed?.bookings.length ? null : (
  <details className="ad-card" data-testid="payments-owed-list">
    <summary><b>Balances due on confirmed bookings · {money(owed.total)}</b></summary>
    {owed.bookings.map((b) => <div className="ad-row" key={b.booking_id}><span>{b.date} · {b.name} · {b.treatment}<small className="muted-sm"> · paid {money(b.paid)} of {money(b.price)}</small></span><strong>{money(b.balance)}</strong></div>)}
  </details>
));

const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 29); return ymd(d); };

export default function Payments() {
  const { data } = useAdmin("payments", "/admin/payments");
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(() => ymd(new Date()));
  const [method, setMethod] = useState("all");
  const [view, setView] = useState("chart");
  const [filter, setFilter] = useState("all");
  const { data: owed } = useAdmin(`balances-${from}-${to}`, `/admin/payments/balances?start=${from}&end=${to}`);
  const qc = useQueryClient();
  const inRange = useMemo(() => (data || []).filter((p) => {
    const d = saleDay(p);
    return d >= from && d <= to && (method === "all" || (method === "none" ? !p.provider : p.provider === method));
  }), [data, from, to, method]);
  const list = useMemo(() => inRange.filter((p) => filter === "all" || p.status === filter), [inRange, filter]);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-payments">
      <PageHead eyebrow="Revenue" title="Service sales" />
      <p className="muted-sm">Deposits and balances for treatments — shop sales stay under Shop sales. Balances due are for confirmed appointments dated in this range.</p>
      <ReportFilters {...{ from, to, setFrom, setTo, method, setMethod, view, setView }} />
      <Totals list={inRange} owed={owed} />
      {view === "chart" ? <SalesChart orders={inRange.map(asOrder)} from={from} to={to} title="Collected" /> : <PaymentsTable list={inRange} from={from} to={to} />}
      <TopTreatments list={inRange} />
      <Owed owed={owed} />
      <DataTools section="payments" label="service payments" exportUrl={`/admin/payments/export?start=${from}&end=${to}`} filename={`waxmoi-service-sales_${from}_${to}.xlsx`} onPurged={refresh} />
      <h3 className="sales-orders-title">Payments</h3>
      <Chips options={[["all", "All"], ...STATUSES.map((s) => [s, STATUS_LABEL[s]])]} value={filter} onChange={setFilter} testPrefix="payments-filter" />
      {!list.length ? <Empty>No booking payments in this range.</Empty> : <div className="req-list">{list.map((p) => <PaymentRow key={`${p.id}-${p.admin_notes || ""}`} p={p} onChange={refresh} />)}</div>}
    </div>
  );
}
